import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getClientCategorisationQuestionnaire, postClientCategorisationQuestionnaire } from '@/services/api/profile/client-categorisation-questionnaire';

const profileClientCategorisationQuestionnaireQueryKey = (pid: UserPid) => ['profile', 'client-categorisation-questionnaire', pid];

const profileClientCategorisationQuestionnaireQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: getClientCategorisationQuestionnaire,
    queryKey: profileClientCategorisationQuestionnaireQueryKey(pid),
  });

const useProfileClientCategorisationQuestionnaire = (enabled = true) => {
  const { pid } = useUserPid();
  const { enabled: queryOptionsEnabled, ...queryOptions } = profileClientCategorisationQuestionnaireQueryOptions(pid);

  return useQuery({
    ...queryOptions,
    enabled: queryOptionsEnabled && enabled,
  });
};

const useProfileClientCategorisation = (enabled = true) => {
  const { data, ...queryResult } = useProfileClientCategorisationQuestionnaire(enabled);

  return {
    ...queryResult,
    data: data?.c ?? null,
  };
};

const useProfileClientCategorisationQuestionnaireMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postClientCategorisationQuestionnaire,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: profileClientCategorisationQuestionnaireQueryKey(pid) });
    },
  });
};

export { useProfileClientCategorisation, useProfileClientCategorisationQuestionnaire, useProfileClientCategorisationQuestionnaireMutation };
